import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import type { FilePreviewDialogData } from './file-preview-dialog.component';

/**
 * The one way to open <app-file-preview-dialog>.
 *
 * Every surface that can show a file inline — the smart inbox timeline, the
 * price journal, the observations table, the candidate tooltips — needs the
 * same overlay at the same size. Written out at each call site, one of them
 * ends up opening narrower than the rest, or losing `autoFocus: false` and
 * stealing the caret. The config lives here; callers bring only the fetcher.
 */
@Injectable({ providedIn: 'root' })
export class FilePreviewService {
  constructor(private dialog: MatDialog) {}

  /**
   * The component is loaded on the click, not imported at the top.
   *
   * Two reasons, and either alone would be enough. The dialog is declared with
   * `SharedModule`, which re-exports the candidate panel — a caller of this
   * service — so a static import closes a cycle that leaves `SharedModule`
   * undefined at evaluation time. And the previewer it renders pulls in
   * SheetJS and the PDF viewer, which nothing needs until a file is actually
   * opened.
   *
   * The promise is deliberately not surfaced: no caller has anything to do
   * after the overlay is up.
   */
  open(data: FilePreviewDialogData): void {
    void import('./file-preview-dialog.component').then(({ FilePreviewDialogComponent }) => {
      this.dialog.open(FilePreviewDialogComponent, {
        data,
        panelClass: 'file-preview-dialog-panel',
        width: '900px',
        maxWidth: '90vw',
        autoFocus: false,
      });
    });
  }
}
